// Source Documents — list page.
//
// Lives at /admin/knowledge/sources. Mirrors the Tenants + KB list patterns:
// PageHeader → stat cards → filter chips → table → footer count.
//
// Permission gate: `sources:write` (super_admin + content_editor only),
// enforced both by the nav model + the backend handler.

import { useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, StatCard, TextInput } from '@gc-coach/design-system';
import type {
  SourceDocumentLanguage,
  SourceDocumentStatusFilter,
  SourceDocumentSummary,
} from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import { formatRelativeTime } from '../users/time.js';
import { useSourceDocuments } from './useSourceDocuments.js';
import styles from './SourceDocuments.module.css';

const LANGUAGES: ReadonlyArray<SourceDocumentLanguage> = ['en', 'fr'];

export function SourceDocumentsListPage() {
  const navigate = useNavigate();
  const { t, tt } = useI18n();

  const [statusFilter, setStatusFilter] = useState<SourceDocumentStatusFilter>('all');
  const [langFilter, setLangFilter] = useState<SourceDocumentLanguage | ''>('');
  const [q, setQ] = useState('');

  const query = useMemo(
    () => ({
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(langFilter ? { language: langFilter } : {}),
      ...(q.trim() ? { q } : {}),
    }),
    [statusFilter, langFilter, q],
  );

  const { data, isLoading } = useSourceDocuments(query);
  const docs = useMemo(() => data?.documents ?? [], [data]);
  const counts = data?.counts ?? {
    total: 0,
    uploaded: 0,
    processing: 0,
    ingested: 0,
    failed: 0,
    removed: 0,
  };

  // Marketing-style empty state only when truly zero docs AND no filters
  const showMarketingEmpty =
    !isLoading &&
    counts.total === 0 &&
    statusFilter === 'all' &&
    !langFilter &&
    !q.trim();

  return (
    <AdminShell
      activeKey="sources"
      breadcrumb={[t('sources.breadcrumbKnowledge'), t('sources.breadcrumbSources')]}
      state={{ kind: 'normal' }}
    >
      <PageHeader
        title={t('sources.title')}
        sub={tt('sources.subTemplate', {
          total: counts.total,
          processing: counts.processing,
          ingested: counts.ingested,
        })}
        actions={
          <Button kind="primary" onClick={() => navigate('/admin/knowledge/sources/new')}>
            {t('sources.uploadButton')}
          </Button>
        }
      />

      {/* Stat row — click a card to filter */}
      <div className={styles.statRow}>
        <StatCard
          label={t('sources.statTotal')}
          value={counts.total.toString()}
          onClick={() => setStatusFilter('all')}
        />
        <StatCard
          label={t('sources.statUploaded')}
          value={counts.uploaded.toString()}
          onClick={() => setStatusFilter('uploaded')}
        />
        <StatCard
          label={t('sources.statProcessing')}
          value={counts.processing.toString()}
          onClick={() => setStatusFilter('processing')}
        />
        <StatCard
          label={t('sources.statIngested')}
          value={counts.ingested.toString()}
          onClick={() => setStatusFilter('ingested')}
        />
      </div>

      {/* Filters */}
      <div className={styles.filterRow}>
        <div className={styles.chips} role="group" aria-label="Status filter">
          <FilterChip
            label={tt('sources.chipAllTemplate', { n: counts.total - counts.removed })}
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          />
          <FilterChip
            label={tt('sources.chipUploadedTemplate', { n: counts.uploaded })}
            active={statusFilter === 'uploaded'}
            onClick={() => setStatusFilter('uploaded')}
          />
          <FilterChip
            label={tt('sources.chipProcessingTemplate', { n: counts.processing })}
            active={statusFilter === 'processing'}
            onClick={() => setStatusFilter('processing')}
          />
          <FilterChip
            label={tt('sources.chipIngestedTemplate', { n: counts.ingested })}
            active={statusFilter === 'ingested'}
            onClick={() => setStatusFilter('ingested')}
          />
          <FilterChip
            label={tt('sources.chipFailedTemplate', { n: counts.failed })}
            active={statusFilter === 'failed'}
            onClick={() => setStatusFilter('failed')}
          />
          <FilterChip
            label={tt('sources.chipRemovedTemplate', { n: counts.removed })}
            active={statusFilter === 'removed'}
            onClick={() => setStatusFilter('removed')}
          />
        </div>

        <div className={styles.filterRight}>
          <div className={styles.langFilter}>
            <span className={styles.langFilterLabel}>{t('sources.langFilterLabel')}</span>
            <select
              className={styles.langSelect}
              value={langFilter}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setLangFilter(e.target.value as SourceDocumentLanguage | '')
              }
            >
              <option value="">{t('sources.langFilterAll')}</option>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <TextInput
            placeholder={t('sources.searchPlaceholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className={styles.search}
          />
        </div>
      </div>

      {/* Table or marketing empty */}
      {showMarketingEmpty ? (
        <div className={styles.emptyMarketing}>
          <h2>{t('sources.emptyMarketingTitle')}</h2>
          <p>{t('sources.emptyMarketingBody')}</p>
          <Button kind="primary" onClick={() => navigate('/admin/knowledge/sources/new')}>
            {t('sources.uploadButton')}
          </Button>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('sources.colTitle')}</th>
                <th>{t('sources.colAuthor')}</th>
                <th>{t('sources.colLang')}</th>
                <th>{t('sources.colStatus')}</th>
                <th>{t('sources.colChunks')}</th>
                <th>{t('sources.colSize')}</th>
                <th>{t('sources.colUploadedBy')}</th>
                <th>{t('sources.colUploaded')}</th>
              </tr>
            </thead>
            <tbody>
              {docs.length === 0 ? (
                <tr>
                  <td colSpan={8} className={styles.empty}>
                    <strong>{t('sources.emptyTitle')}</strong>
                    <span>{t('sources.emptyBody')}</span>
                  </td>
                </tr>
              ) : (
                docs.map((d) => (
                  <DocumentRow key={d.id} doc={d} onClick={() => navigate(`/admin/knowledge/sources/${d.id}`)} />
                ))
              )}
            </tbody>
          </table>
          <div className={styles.tableFooter}>
            {tt('sources.footerCountTemplate', { n: docs.length })}
          </div>
        </div>
      )}
    </AdminShell>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function DocumentRow({
  doc,
  onClick,
}: {
  doc: SourceDocumentSummary;
  onClick: () => void;
}) {
  const { t } = useI18n();
  return (
    <tr className={styles.row} onClick={onClick}>
      <td>
        <div className={styles.titleCell}>{doc.title}</div>
      </td>
      <td className={styles.cellMuted}>{doc.author}</td>
      <td>
        <span className={styles.langChip}>{doc.language.toUpperCase()}</span>
      </td>
      <td>
        <span className={statusPillClass(doc.status, styles)}>
          {statusText(doc.status, t)}
        </span>
      </td>
      <td className={styles.cellMono}>
        {doc.chunk_count !== null ? doc.chunk_count : '—'}
      </td>
      <td className={styles.cellMono}>{formatBytes(doc.file_size_bytes)}</td>
      <td className={styles.cellMuted}>{doc.uploaded_by}</td>
      <td className={styles.cellMuted}>{formatRelativeTime(doc.uploadedAt)}</td>
    </tr>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function statusPillClass(s: string, cls: Record<string, string>): string {
  const base = cls['statusPill'] ?? '';
  if (s === 'uploaded') return `${base} ${cls['statusUploaded'] ?? ''}`;
  if (s === 'processing') return `${base} ${cls['statusProcessing'] ?? ''}`;
  if (s === 'ingested') return `${base} ${cls['statusIngested'] ?? ''}`;
  if (s === 'failed') return `${base} ${cls['statusFailed'] ?? ''}`;
  return `${base} ${cls['statusRemoved'] ?? ''}`;
}

function statusText(s: string, t: (k: string) => string): string {
  if (s === 'uploaded') return t('sources.statusUploaded');
  if (s === 'processing') return t('sources.statusProcessing');
  if (s === 'ingested') return t('sources.statusIngested');
  if (s === 'failed') return t('sources.statusFailed');
  return t('sources.statusRemoved');
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Local FilterChip — mirrors the helper used in KBListPage / UsersPage /
// TenantsPage. Not exported from the design system; copied here so the
// shared styles in SourceDocuments.module.css carry the chip classes too.
function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={[styles['chip'], active ? styles['chipActive'] : null]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}
